// Address picking from Royal Mail PAF, plain DOM, no React: it ships as its
// own small script (client/packs/address-pick.js), added to the page only
// while the switch is on (AddressLookup, docs/ADDRESS_LOOKUP.md), so the main
// bundle does not carry it. It listens on the document, so it survives React
// re-rendering the boxes.
//
// Book & pay's Car location box (installCarLocation, 19 Oct 2026): typing a
// street (3 characters or more, 250 ms after the last key) lists the
// provider's free typeahead suggestions; a whole postcode lists its
// addresses, and a house number or name typed with it ("SW6 1RE 45")
// narrows that list as they type, best matches first (22 Oct 2026: both
// payers of 21 Oct scrolled to rows 14 and 18). The list sits in the page under the box (StepBook renders the
// empty #book-carLocation-list), so it pushes what is below down and never
// covers the next field; it shows only while the box has the focus, and a
// tap on a row keeps the focus (mousedown is cancelled) so a pick on a phone
// lands before the box blurs. A pick is handed to the page as a
// `gwmc:car-location` event (lib/car-location.js); "No match" or a lookup
// that does not answer offers to enter it manually
// (`gwmc:car-location-manual`): the same box as plain free text
// (data-free), which this script then leaves alone.
//
// The admin manual-order form (installAdminPick): a whole postcode in its
// postcode box opens "Pick your address" under it; a tap fills the postcode
// and the house number box with the PAF line.
import {
  ADDRESS_PICK_PAIRS, CAR_LOCATION_ID, CAR_LOCATION_LIST_ID, DEBOUNCE_MS, FILTER_HINT_MIN, filteredProps, labelFor,
  lengthBucket, makeAddressFetcher, makeCarLocationLookup, makeJsonFetcher, notCoveredProps, pairFor, pickedProps,
  pickValues, resolveUrl
} from '../../lib/address-pick.js'
import { MANUAL_EVENT, PICK_EVENT } from '../../lib/car-location.js'

const CSS = '.gw-ap{position:absolute;z-index:1400;background:#fff;border:1px solid #d5d9de;border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,.14);max-height:260px;overflow-y:auto;font:15px/1.3 inherit;-webkit-overflow-scrolling:touch}' +
  '.gw-ap p,.gw-cl p{margin:0;padding:10px 14px 6px;font-size:13px;color:var(--mute,#5f6b76)}' +
  '.gw-ap button,.gw-cl button{display:block;width:100%;text-align:left;background:none;border:0;border-top:1px solid var(--line,#eef0f2);padding:11px 14px;font:inherit;color:var(--ink,#13202b);cursor:pointer}' +
  '.gw-ap button:hover,.gw-ap button:focus,.gw-cl button:hover,.gw-cl button[aria-selected=true]{background:var(--accent-soft,#f2f7f4);outline:none}' +
  '.gw-ap .gw-ap-x,.gw-cl .gw-cl-x{color:var(--mute,#5f6b76);font-size:14px}' +
  '.gw-cl{margin-top:6px;background:var(--paper,#fff);border:1px solid var(--line,#d5d9de);border-radius:10px;max-height:264px;overflow-y:auto;font-size:15px;line-height:1.3;-webkit-overflow-scrolling:touch}' +
  '.gw-cl[hidden]{display:none}'

const track = (name, props) => {
  try { if (typeof window.gwmcTrackCarLocation === 'function') window.gwmcTrackCarLocation(name, props) } catch (e) { /* never break the form */ }
}

// React keeps its own copy of an input's value: set it through the native
// setter and announce it, so ActiveAdmin's form (and React) see a change.
const setValue = (input, value) => {
  if (!input || value == null) return
  const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set
  setter.call(input, value)
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))
}

const button = (text, className) => {
  const b = document.createElement('button')
  b.type = 'button'
  b.textContent = text
  if (className) b.className = className
  // Keep the focus in the box: a tap on a row must not blur it (which
  // closes the list) before the click lands.
  b.addEventListener('mousedown', e => e.preventDefault())
  return b
}

export const installCarLocation = fetchFn => {
  const lookup = makeCarLocationLookup(fetchFn)
  const getJson = makeJsonFetcher(fetchFn)
  let timer = null
  let asked = 0
  let picking = 0
  let blurTimer = null
  let rows = []
  let active = -1
  let focused = false
  // The list on screen came from a postcode narrowed by a house number.
  let narrowed = false
  const shown = new Set()
  const filtered = new Set()

  const box = () => document.getElementById(CAR_LOCATION_ID)
  const list = () => document.getElementById(CAR_LOCATION_LIST_ID)

  const close = () => {
    clearTimeout(timer)
    asked += 1
    narrowed = false
    rows = []
    active = -1
    const l = list()
    if (l) { l.textContent = ''; l.hidden = true }
    const b = box()
    if (b) { b.setAttribute('aria-expanded', 'false'); b.removeAttribute('aria-activedescendant') }
  }

  const show = (head, items) => {
    const l = list()
    const b = box()
    if (!l || !b || document.activeElement !== b) return close()
    l.textContent = ''
    l.className = 'gw-cl'
    if (head) {
      const p = document.createElement('p')
      p.textContent = head
      l.appendChild(p)
    }
    rows = items
    active = -1
    items.forEach((item, i) => {
      const r = button(item.label, item.className)
      r.id = `${CAR_LOCATION_LIST_ID}-${i}`
      r.setAttribute('role', 'option')
      r.setAttribute('aria-selected', 'false')
      r.addEventListener('click', () => item.run(i))
      l.appendChild(r)
    })
    l.hidden = false
    b.setAttribute('aria-expanded', 'true')
  }

  const manual = reason => {
    close()
    document.dispatchEvent(new CustomEvent(MANUAL_EVENT, { detail: { reason } }))
  }

  const manualRow = (label, reason) => ({ label, className: 'gw-cl-x', run: () => manual(reason) })

  const picked = (entry, source, position) => {
    const wasNarrowed = narrowed
    close()
    const props = { ...pickedProps({ entry, source, position }), ...(wasNarrowed ? { filtered: true } : {}) }
    track('picked', props)
    if (!props.covered) track('not_covered', notCoveredProps(entry))
    document.dispatchEvent(new CustomEvent(PICK_EVENT, { detail: { entry, source, position } }))
  }

  const pick = (item, i) => {
    if (item.entry) return picked(item.entry, 'postcode', i + 1)
    // A suggestion is fetched whole once picked: the one paid call. It lands
    // even if the box loses the focus meanwhile, unless they typed again.
    const mine = ++picking
    const typed = box() && box().value
    show('Finding the address', [])
    getJson(resolveUrl(item.id)).then(entry => {
      if (mine !== picking || !box() || box().value !== typed) return
      if (entry && entry.line && entry.postcode) picked(entry, 'autocomplete', i + 1)
      else manual('error')
    })
  }

  const offer = text => {
    const mine = ++asked
    lookup(text).then(found => {
      const b = box()
      if (mine !== asked || !b || b.value !== text) return
      narrowed = false
      if (!found) return close()
      if (found.failed) return show(null, [manualRow("We can't look addresses up right now. Enter it manually", 'error')])
      if (!found.items.length) return show(null, [manualRow("No match. Enter it manually", 'no_results')])
      const key = `${found.kind}|${lengthBucket(text.trim().length)}`
      if (!shown.has(key)) {
        shown.add(key)
        track('suggestions_shown', { count: found.items.length, query_length: lengthBucket(text.trim().length), kind: found.kind })
      }
      const rowsFor = items => items.map(item => ({ label: item.label, run: i => pick(item, i) }))
      if (found.filter) {
        // Once per thing typed after the postcode; the count, never the text.
        if (!filtered.has(text.trim())) {
          filtered.add(text.trim())
          track('filtered', filteredProps({ count: found.matched ? found.items.length : 0, total: found.total, filter: found.filter }))
        }
        if (!found.matched) {
          show(`Nothing matches "${found.filter}". All ${found.total} addresses:`, [...rowsFor(found.items), manualRow('Not listed? Enter it manually', 'no_results')])
          narrowed = false
          return
        }
        show(found.items.length === 1 ? 'Your address' : `Best matches (${found.items.length} of ${found.total})`, rowsFor(found.items))
        narrowed = true
        return
      }
      const head = found.kind !== 'postcode' ? null
        : found.items.length >= FILTER_HINT_MIN ? 'Pick your address, or type your house number or name after the postcode'
          : 'Pick your address'
      show(head, rowsFor(found.items))
    })
  }

  const highlight = next => {
    const l = list()
    if (!l || !rows.length) return
    active = (next + rows.length) % rows.length
    l.querySelectorAll('[role="option"]').forEach((r, i) => r.setAttribute('aria-selected', i === active ? 'true' : 'false'))
    const r = document.getElementById(`${CAR_LOCATION_LIST_ID}-${active}`)
    if (r) {
      box().setAttribute('aria-activedescendant', r.id)
      try { r.scrollIntoView({ block: 'nearest' }) } catch (e) { /* older browsers */ }
    }
  }

  // The box as plain free text ("Enter it manually", data-free): no list.
  const isFree = t => t.getAttribute('data-free') === 'true'

  document.addEventListener('input', e => {
    const t = e.target
    if (!t || t.id !== CAR_LOCATION_ID || !e.isTrusted) return
    if (isFree(t)) return close()
    clearTimeout(timer)
    const text = t.value
    timer = setTimeout(() => offer(text), DEBOUNCE_MS)
  }, true)

  document.addEventListener('focusin', e => {
    const t = e.target
    if (!t || t.id !== CAR_LOCATION_ID || isFree(t)) return
    clearTimeout(blurTimer)
    if (!focused) { focused = true; track('focused', {}) }
    if (t.value && !rows.length) offer(t.value)
  }, true)

  // The list goes with the focus, a moment later: a tap on a row that did
  // blur the box (a browser that ignores the cancelled mousedown) still
  // lands its click first.
  document.addEventListener('focusout', e => {
    if (!e.target || e.target.id !== CAR_LOCATION_ID) return
    clearTimeout(blurTimer)
    blurTimer = setTimeout(close, 200)
  }, true)

  document.addEventListener('keydown', e => {
    if (!e.target || e.target.id !== CAR_LOCATION_ID) return
    if (e.key === 'Escape') return close()
    if (!rows.length) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      highlight(active + (e.key === 'ArrowDown' ? 1 : -1))
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault()
      rows[active].run(active)
    }
  }, true)
}

// The admin manual-order form: a floating "Pick your address" under the
// postcode box, as before 19 Oct 2026.
export const installAdminPick = fetchFn => {
  const fetchList = makeAddressFetcher(fetchFn)
  let box = null
  let anchor = null
  let timer = null

  const close = () => {
    if (box) box.remove()
    box = null
    anchor = null
  }

  const place = () => {
    if (!box || !anchor || !anchor.isConnected) return close()
    const r = anchor.getBoundingClientRect()
    box.style.left = `${r.left + window.scrollX}px`
    box.style.top = `${r.bottom + window.scrollY + 4}px`
    box.style.width = `${Math.max(r.width, 260)}px`
  }

  const open = (input, pair, list) => {
    close()
    if (!list || !list.length) return
    box = document.createElement('div')
    box.className = 'gw-ap'
    box.setAttribute('role', 'listbox')
    box.setAttribute('data-address-pick', '')
    const head = document.createElement('p')
    head.textContent = 'Pick your address'
    box.appendChild(head)
    list.forEach(entry => {
      const b = button(labelFor(entry))
      b.setAttribute('role', 'option')
      b.addEventListener('click', () => {
        const values = pickValues(entry)
        setValue(document.getElementById(pair.postcode), values.postcode)
        setValue(document.getElementById(pair.house), values.house)
        close()
      })
      box.appendChild(b)
    })
    const x = button('Not listed? Type it in', 'gw-ap-x')
    x.addEventListener('click', () => {
      close()
      const house = document.getElementById(pair.house)
      if (house) house.focus()
    })
    box.appendChild(x)
    anchor = input
    document.body.appendChild(box)
    place()
  }

  const offer = (input, pair) => {
    const pc = document.getElementById(pair.postcode)
    const value = pc && pc.value
    fetchList(value).then(list => {
      if (!pc || pc.value !== value || document.activeElement !== input) return
      open(input, pair, list)
    })
  }

  document.addEventListener('input', e => {
    const t = e.target
    const pair = t && t.id && pairFor(t.id)
    if (!pair || !e.isTrusted) return
    if (t.id === pair.house) return close()
    clearTimeout(timer)
    timer = setTimeout(() => offer(t, pair), DEBOUNCE_MS)
  }, true)

  document.addEventListener('focusin', e => {
    const t = e.target
    const pair = t && t.id && pairFor(t.id)
    if (!pair || box) return
    if (t.id === pair.house && t.value) return
    offer(t, pair)
  }, true)

  document.addEventListener('pointerdown', e => {
    if (!box || box.contains(e.target) || e.target === anchor) return
    close()
  }, true)
  document.addEventListener('keydown', e => { if (e.key === 'Escape') close() }, true)
  window.addEventListener('resize', place)
}

export const installAddressPick = () => {
  if (typeof document === 'undefined' || window.__gwAddressPick) return
  window.__gwAddressPick = true
  const fetchFn = typeof fetch === 'function' ? fetch.bind(window) : null
  const style = document.createElement('style')
  style.textContent = CSS
  document.head.appendChild(style)
  installCarLocation(fetchFn)
  installAdminPick(fetchFn)
}

export { ADDRESS_PICK_PAIRS }
